import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Quote as QuoteIcon, ArrowLeft, Send } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useCompany, fmtNipc, brandShort } from "../context/CompanyContext";

const REGIONS = [
  "Funchal", "Câmara de Lobos", "Ribeira Brava", "Ponta do Sol",
  "Calheta", "Porto Moniz", "São Vicente", "Santana",
  "Machico", "Santa Cruz", "Caniço", "Porto Santo",
];

/**
 * /testemunhos — public testimonial wall.
 * Visitors read approved reviews and can submit their own at the bottom.
 * Newest first; rating + first name + region badge for each card.
 * After submit the testimonial enters admin moderation queue.
 */
export default function Testimonials() {
  const { company } = useCompany();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({ total: 0, average_rating: 5 });
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [searchParams] = useSearchParams();
  const prefillAcc = (searchParams.get("acc") || "").slice(0, 80);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/public/testimonials");
      setItems(data.items || []);
      setSummary(data.summary || { total: 0, average_rating: 5 });
    } catch (_) {} finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  return (
    <div className="min-h-screen bg-[#031c18] text-[#fdfbf7]">
      <header className="px-6 lg:px-12 py-6 border-b border-[#1a4640] flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#a0b2af] hover:text-[#d4af37]"
              data-testid="testimonials-back">
          <ArrowLeft size={14} /> Voltar à página inicial
        </Link>
        <div className="font-display text-xl tracking-wide text-[#d4af37]">{company.trade_name}</div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-12 py-16 space-y-16">
        {/* Hero */}
        <section className="text-center space-y-5">
          <div className="eyebrow text-[#d4af37]">Vozes dos hosts</div>
          <h1 className="font-display text-5xl lg:text-7xl leading-[1.05]">
            O que dizem<br />
            <span className="text-[#d4af37]">de nós.</span>
          </h1>
          <div className="flex items-center justify-center gap-6 pt-4 flex-wrap">
            <div className="flex items-center gap-2 text-[#d4af37]">
              {[1,2,3,4,5].map(i => (
                <Star key={i} size={20}
                      fill={i <= Math.round(summary.average_rating) ? "#d4af37" : "transparent"}
                      stroke="#d4af37" />
              ))}
              <span className="font-display text-2xl ml-2">{summary.average_rating.toFixed(1)}</span>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0b2af]">
              {summary.total} testemunhos publicados
            </div>
          </div>
        </section>

        {/* Wall */}
        <section data-testid="testimonials-wall">
          {loading ? (
            <div className="text-center text-[#a0b2af]">A carregar testemunhos…</div>
          ) : items.length === 0 ? (
            <div className="text-center text-[#a0b2af] italic py-12">
              Seja o primeiro a deixar um testemunho.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {items.map(t => <TestimonialCard key={t.id} t={t} />)}
            </div>
          )}
        </section>

        {/* Submit form */}
        <section className="lux-card p-6 lg:p-10 space-y-6 max-w-2xl mx-auto"
                 data-testid="testimonials-form-section">
          <div>
            <div className="eyebrow">Partilhar a sua experiência</div>
            <h2 className="font-display text-3xl mt-2">O seu testemunho.</h2>
            <p className="text-sm text-[#a0b2af] mt-3 max-w-md">
              Aparecerá nesta página após uma rápida moderação. Não é obrigatório
              identificar-se — pode usar apenas o primeiro nome.
            </p>
          </div>

          {submitted ? (
            <div className="text-[#d4af37] text-sm border border-[#d4af37]/40 bg-[#d4af37]/5 px-4 py-4 rounded-sm"
                 data-testid="testimonials-thanks">
              Obrigado! O seu testemunho foi recebido e será publicado após moderação.
            </div>
          ) : (
            <SubmitForm onSent={() => setSubmitted(true)} initialAccommodation={prefillAcc} />
          )}
        </section>
      </main>

      <footer className="border-t border-[#1a4640] mt-12 px-6 lg:px-12 py-8 text-xs text-[#a0b2af] text-center">
        {company.legal_name}{company.nipc ? ` · NIPC ${fmtNipc(company.nipc)}` : ""}
      </footer>
    </div>
  );
}

function TestimonialCard({ t }) {
  return (
    <article className="lux-card p-6 space-y-4 relative" data-testid={`testimonial-${t.id}`}>
      <QuoteIcon size={20} className="text-[#d4af37]/40 absolute top-5 right-5" />
      <div className="flex items-center gap-1">
        {[1,2,3,4,5].map(i => (
          <Star key={i} size={13} fill={i <= t.rating ? "#d4af37" : "transparent"} stroke="#d4af37" />
        ))}
      </div>
      <p className="text-sm leading-relaxed italic text-[#fdfbf7]/90">«{t.quote}»</p>
      <div className="pt-3 border-t border-[#1a4640] text-[11px] uppercase tracking-widest">
        <div className="text-[#d4af37]">
          {t.first_name} {t.accommodation && <span className="text-[#fdfbf7]/60">· {t.accommodation}</span>}
        </div>
        <div className="text-[#a0b2af]/80 mt-0.5">{t.region}, Madeira</div>
      </div>
    </article>
  );
}

function SubmitForm({ onSent, initialAccommodation = "" }) {
  const [form, setForm] = useState({
    first_name: "", region: "Funchal", rating: 5,
    accommodation: initialAccommodation, quote: "",
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    if (form.quote.trim().length < 20) return toast.error("Conte-nos um pouco mais (mín. 20 caracteres).");
    setBusy(true);
    try {
      await api.post("/public/testimonials", {
        ...form,
        first_name: form.first_name.trim() || null,
        accommodation: form.accommodation.trim() || null,
      });
      onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="testimonials-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Primeiro nome (opcional)" value={form.first_name}
               onChange={v => setForm(f => ({ ...f, first_name: v }))}
               placeholder="Maria" testId="t-name" maxLength={40} />
        <div className="space-y-1.5">
          <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Região *</label>
          <select className="lux-input w-full" value={form.region}
                  onChange={e => setForm(f => ({ ...f, region: e.target.value }))}
                  data-testid="t-region">
            {REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
      </div>

      <Field label="Nome do alojamento (opcional)" value={form.accommodation}
             onChange={v => setForm(f => ({ ...f, accommodation: v }))}
             placeholder="Casa do Mar — Funchal" testId="t-accommodation" maxLength={80} />

      <div className="space-y-2">
        <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">Avaliação *</label>
        <div className="flex items-center gap-2" data-testid="t-rating">
          {[1,2,3,4,5].map(i => (
            <button type="button" key={i}
                    onClick={() => setForm(f => ({ ...f, rating: i }))}
                    className="p-1"
                    data-testid={`t-star-${i}`}>
              <Star size={28}
                    fill={i <= form.rating ? "#d4af37" : "transparent"}
                    stroke="#d4af37" />
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1.5">
        <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">
          O seu testemunho * <span className="text-[#a0b2af]/60">({form.quote.length}/600)</span>
        </label>
        <textarea className="lux-input w-full min-h-[140px]" value={form.quote}
                  onChange={e => setForm(f => ({ ...f, quote: e.target.value.slice(0, 600) }))}
                  placeholder={`Conte-nos como a ${brandShort()} o ajudou…`}
                  data-testid="t-quote" />
      </div>

      <button type="submit" disabled={busy}
              className="btn-gold w-full py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="t-submit">
        <Send size={14} /> {busy ? "A enviar…" : "Enviar testemunho"}
      </button>

      <div className="text-[10px] text-[#a0b2af]/70 text-center">
        Não publicamos links nem testemunhos em maiúsculas. Limite de 1 envio por 15 minutos.
      </div>
    </form>
  );
}

function Field({ label, value, onChange, placeholder, testId, maxLength }) {
  return (
    <div className="space-y-1.5">
      <label className="text-[10px] uppercase tracking-widest text-[#a0b2af]">{label}</label>
      <input className="lux-input w-full" value={value || ""}
             onChange={e => onChange?.(e.target.value)}
             placeholder={placeholder} maxLength={maxLength}
             data-testid={testId} />
    </div>
  );
}
